/**
 * @fileoverview MFA strings barrel + `loadMfaStrings(i18nInstance, locales)`.
 * @package @puffin-core/mfa-ui
 *
 * Mirrors `@puffin-core/auth-shell`'s `loadAuthShellStrings` and `@puffin-core/login-shell`'s
 * `loadLoginStrings`. Consumers should NOT call this by hand — the descriptor in
 * `@puffin-core/i18n-namespaces` registers it for every app at boot (ruling 39), which is what
 * stops app six from forgetting. That is the whole point: the defect this closes was FOUR render
 * sites where three forgot to pass a `t` prop, and a per-call-site fix would have to be repeated
 * every time a fifth appeared.
 *
 * ⚠️ LEAF-ONLY by design. This subpath must not import a component, or `i18n-namespaces` — which
 * every app's bootstrap imports — drags the MFA UI into every app's boot chunk.
 */

import { DEFAULT_MFA_STRINGS, interpolate } from './en.js';
import { MFA_STRINGS_ES } from './es.js';
import { MFA_NAMESPACE } from './namespace.js';

/** The English bundle under a symmetric name. Same frozen object as `DEFAULT_MFA_STRINGS`. */
export const MFA_STRINGS_EN = DEFAULT_MFA_STRINGS;

const BUNDLES = Object.freeze({
  en: MFA_STRINGS_EN,
  es: MFA_STRINGS_ES,
});

/** Locales this package ships. `createAppI18n` derives its list from these keys. */
export const SUPPORTED_LOCALES = Object.freeze(Object.keys(BUNDLES));

/**
 * Register the MFA bundles on a react-i18next instance.
 *
 * @param {Object} i18nInstance - a react-i18next instance
 * @param {string|string[]} [locales] - locale or locales to register
 * @returns {Object} the same instance, for chaining
 */
export function loadMfaStrings(i18nInstance, locales = 'en') {
  if (!i18nInstance || typeof i18nInstance.addResourceBundle !== 'function') {
    throw new Error('loadMfaStrings: expected a react-i18next instance with addResourceBundle()');
  }
  const list = Array.isArray(locales) ? locales : [locales];
  list.forEach((locale) => {
    const bundle = BUNDLES[locale];
    if (!bundle) {
      throw new Error(
        `loadMfaStrings: unsupported locale "${locale}". Supported: ${SUPPORTED_LOCALES.join(', ')}`,
      );
    }
    // Deep merge, do NOT overwrite — an app that has already injected its own `mfa.*` override
    // keeps it.
    i18nInstance.addResourceBundle(locale, MFA_NAMESPACE, bundle, true, false);
  });
  return i18nInstance;
}

export { DEFAULT_MFA_STRINGS, MFA_STRINGS_ES, MFA_NAMESPACE, interpolate };
export { resolveString } from './resolveString.js';
